import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../services/ApiService';
import { generateMetaPurchaseEventId } from '../utils/metaPurchaseEventId';
import {
  META_PURCHASE_EVENT_NAME,
  metaCustomerTypeFromEsMayorista,
} from '../constants/metaPixel';

const CarritoContext = createContext();

export const useCarrito = () => {
  const context = useContext(CarritoContext);
  if (!context) {
    throw new Error('useCarrito debe ser usado dentro de un CarritoProvider');
  }
  return context;
};

export const CarritoProvider = ({ children }) => {
  const [carritoItems, setCarritoItems] = useState(() => {
    // Cargar del localStorage al iniciar
    const savedCarrito = localStorage.getItem('carrito');
    return savedCarrito ? JSON.parse(savedCarrito) : [];
  });

  const [showCarritoSidebar, setShowCarritoSidebar] = useState(false);

  // Guardar en localStorage cada vez que cambia el carrito
  useEffect(() => {
    localStorage.setItem('carrito', JSON.stringify(carritoItems));
  }, [carritoItems]);

  // Agregar producto al carrito
  const agregarAlCarrito = (producto) => {
    setCarritoItems(prev => {
      // Verificar si el producto ya existe en el carrito
      const existente = prev.find(item => item.id === producto.id);
      
      if (existente) {
        // Si existe, incrementar cantidad
        return prev.map(item =>
          item.id === producto.id
            ? { ...item, cantidad: item.cantidad + 1 }
            : item
        );
      } else {
        // Si no existe, agregar nuevo item
        return [...prev, { ...producto, cantidad: 1 }];
      }
    });
    // Abrir sidebar automáticamente
    setShowCarritoSidebar(true);
  };

  // Incrementar cantidad
  const incrementarCantidad = (id) => {
    setCarritoItems(prev =>
      prev.map(item =>
        item.id === id ? { ...item, cantidad: item.cantidad + 1 } : item
      )
    );
  };

  // Decrementar cantidad
  const decrementarCantidad = (id) => {
    setCarritoItems(prev =>
      prev.map(item =>
        item.id === id && item.cantidad > 1
          ? { ...item, cantidad: item.cantidad - 1 }
          : item
      )
    );
  };

  // Eliminar item
  const eliminarItem = (id) => {
    setCarritoItems(prev => prev.filter(item => item.id !== id));
  };

  // Vaciar carrito
  const vaciarCarrito = () => {
    setCarritoItems([]);
  };

  // Finalizar compra - enviar al backend y generar mensaje WhatsApp
  const finalizarCompra = async (nombreContacto, telefonoContacto) => {
    try {
      // Validar que se proporcionen los datos de contacto
      if (!nombreContacto || !telefonoContacto || nombreContacto.trim() === '' || telefonoContacto.trim() === '') {
        return { success: false, error: 'Debe proporcionar nombre y teléfono de contacto' };
      }
      
      // Validar que el teléfono tenga al menos 8 caracteres
      if (telefonoContacto.trim().length < 8) {
        return { success: false, error: 'El teléfono debe tener al menos 8 caracteres' };
      }

      // Preparar datos del carrito para enviar al backend
      const toNumber = (val) => {
        const n = Number(val);
        return Number.isFinite(n) ? n : 0;
      };

      const productos = carritoItems.map(item => ({
        id: item.id,
        nombre: item.nombre,
        precio: toNumber(item.precio),
        preciomenor: toNumber(item.preciomenor),
        cantidad: item.cantidad,
      }));

      // Calcular montoTotal y estado requerido por el backend
      const cantidadTotalCalc = carritoItems.reduce((acc, item) => acc + item.cantidad, 0);
      const subtotalMinoristaCalc = carritoItems.reduce((acc, item) => acc + (toNumber(item.preciomenor) * item.cantidad), 0);
      const subtotalMayoristaCalc = carritoItems.reduce((acc, item) => acc + (toNumber(item.precio) * item.cantidad), 0);
      const esMayoristaCalc = subtotalMayoristaCalc >= 60000 && cantidadTotalCalc >= 6;
      const montoTotal = esMayoristaCalc ? subtotalMayoristaCalc : subtotalMinoristaCalc;
      const metaCustomerType = metaCustomerTypeFromEsMayorista(esMayoristaCalc);

      const metaEventId = generateMetaPurchaseEventId();

      // Enviar compra al backend (el backend se encargará de validar stock, descontarlo y crear el pedido)
      console.log('[finalizarCompra] Enviando productos:', productos);
      const respuesta = await ApiService.post('/pedidos', {
        productos,
        montoTotal,
        descuento: 0,
        estado: 'CREADO',
        nombreContacto: nombreContacto.trim(),
        telefonoContacto: telefonoContacto.trim(),
        metaEventId,
        metaEventName: META_PURCHASE_EVENT_NAME,
        metaCustomerType,
      });
      console.log('[finalizarCompra] Respuesta del backend:', respuesta);

      // Generar mensaje de WhatsApp
      const mensajeWhatsApp = generarMensajeWhatsApp(nombreContacto.trim(), telefonoContacto.trim());
      
      // Vaciar carrito después de compra exitosa
      setCarritoItems([]);
      
      return {
        success: true,
        mensajeWhatsApp,
        respuesta,
        metaEventId,
        metaEventName: META_PURCHASE_EVENT_NAME,
        metaCustomerType,
      };
    } catch (error) {
      console.error('Error al finalizar compra:', error);
      console.error('Error status:', error?.status);
      console.error('Error payload:', error?.payload);
      
      // Extraer mensaje de error del backend
      let mensajeError = 'Error al procesar la compra';
      let stockErrors = null; // Errores de stock por producto
      
      if (error?.payload) {
        mensajeError = error.payload.message || error.payload.error || error.payload.msg || JSON.stringify(error.payload);
        
        // Si hay detalles de errores de stock, extraerlos
        if (error.payload.details && Array.isArray(error.payload.details)) {
          stockErrors = {};
          error.payload.details.forEach(detail => {
            if (detail.productId && detail.error) {
              stockErrors[detail.productId] = detail.error;
            }
          });
        }
      } else if (error?.message) {
        mensajeError = error.message;
      }
      
      return { success: false, error: mensajeError, stockErrors };
    }
  };

  // Generar mensaje de WhatsApp simplificado
  const generarMensajeWhatsApp = (nombreContacto, telefonoContacto) => {
    const cantidadTotal = carritoItems.reduce((total, item) => total + item.cantidad, 0);
    const toNumber = (val) => {
      const n = Number(val);
      return Number.isFinite(n) ? n : 0;
    };
    // Subtotales (forzando número por si vienen como string desde Firebase)
    const subtotalMinorista = carritoItems.reduce((acc, item) => acc + (toNumber(item.preciomenor) * item.cantidad), 0);
    const subtotalMayorista = carritoItems.reduce((acc, item) => acc + (toNumber(item.precio) * item.cantidad), 0);
    // Regla mayorista: 6 o más productos y mínimo $60000 (evaluado al mayorista)
    const esMayorista = subtotalMayorista >= 60000 && cantidadTotal >= 6;
    const tipoCompra = esMayorista ? 'mayorista' : 'minorista';
    const total = esMayorista ? subtotalMayorista : subtotalMinorista;
    const aliasTransferencia = 'icase.oeste';
    
    let mensaje = `Nuevo Pedido ${tipoCompra.toUpperCase()}%0A%0A`;
    
    // Lista de productos (solo nombre y cantidad)
    carritoItems.forEach(item => {
      mensaje += `• ${item.nombre} x${item.cantidad}%0A`;
    });
    
    mensaje += `%0ATotal: $${total.toLocaleString()}%0A%0A`;
    
    mensaje += `Contacto:%0A`;
    mensaje += `Nombre: ${nombreContacto}%0A`;
    mensaje += `Teléfono: ${telefonoContacto}%0A%0A`;
    
    mensaje += `Para confirmar tu pedido:%0A`;
    mensaje += `Enviá la transferencia a: ${aliasTransferencia}%0A`;
    mensaje += `Y adjuntá el comprobante de pago.%0A%0A`;
    
    mensaje += `¡Gracias!`;
    
    const numeroWhatsapp = '5491156067007';
    return `https://wa.me/${numeroWhatsapp}?text=${mensaje}`;
  };

  // Obtener cantidad total de items
  const cantidadTotal = carritoItems.reduce((total, item) => total + item.cantidad, 0);
  const toNumber = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  };
  const subtotalMinorista = carritoItems.reduce((acc, item) => acc + (toNumber(item.preciomenor) * item.cantidad), 0);
  const subtotalMayorista = carritoItems.reduce((acc, item) => acc + (toNumber(item.precio) * item.cantidad), 0);
  // Regla mayorista exportada al resto de la app (evaluada con precio mayorista)
  const esMayorista = subtotalMayorista >= 60000 && cantidadTotal >= 6;
  const total = esMayorista ? subtotalMayorista : subtotalMinorista;

  return (
    <CarritoContext.Provider value={{
      carritoItems,
      agregarAlCarrito,
      incrementarCantidad,
      decrementarCantidad,
      eliminarItem,
      vaciarCarrito,
      finalizarCompra,
      generarMensajeWhatsApp,
      cantidadTotal,
      subtotalMinorista,
      subtotalMayorista,
      total,
      esMayorista,
      showCarritoSidebar,
      setShowCarritoSidebar
    }}>
      {children}
    </CarritoContext.Provider>
  );
};

